import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../../platform/lang';
import { reportError } from '../../core/telemetry/report';
import { isInstalled } from './InstallPrompt';
import { onQueueChange } from '../../core/offline/syncManager';
import { queueSize } from '../../core/offline/db';

/**
 * Install the app before starting work — a first-run step, like the baseline
 * face scan.
 *
 * WHY IT IS A STEP AND NOT A SUGGESTION. A web page in a browser tab is the
 * first thing the operating system destroys when music, a game or a video wants
 * memory. That is what every 'died' report was: not a crash, an eviction. An
 * installed web app gets its own process and its own budget and is ranked with
 * real applications. Identical code, identical URL — a better container. There
 * is no API to ask the OS for more memory; installing is the entire control we
 * have, so it is worth asking for properly rather than in a banner people
 * scroll past.
 *
 * WHY IT DOES NOT HARD-LOCK. The baseline face scan can refuse to continue
 * because taking a photo always works. Installing does not: Apple gives a site
 * no way to trigger it, some managed handsets forbid it, and an older phone can
 * simply not offer it. A gate that cannot be satisfied would stop a driver
 * working, and no diagnostic is worth a route.
 *
 * So it asks firmly, it is shown every day until it is done, and skipping is
 * RECORDED rather than silent — the office sees who is still running in a tab,
 * by name, without asking anyone. The version string already reports `+tab` or
 * `+home` on every request, so the two agree.
 *
 * If the owner later decides it should hard-lock, that is the `onSkip` button
 * and one line.
 *
 * HE DID, 5 Oct. A driver ran in a Chrome tab for weeks, pressing "I cannot do
 * this now" four times, on a phone that installs it in one tap. The owner: "this
 * should be the first thing the app checks". So `mandatory` (InstallLock, below)
 * has no way past it; a phone that truly cannot install is a call to the office.
 */

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const SKIPPED_KEY = 'jw.installSkippedOn';

/*
 * Has this phone EVER been shown the full instructions?
 *
 * Separate from the daily skip, and the difference matters. Two drivers, two
 * opposite failures:
 *
 *   - Mercedes, 22 Sep: an established driver shown this screen INSTEAD of her
 *     route, at the warehouse, with five stops waiting. It blocked her and the
 *     route never ran. That is why it stopped being a wall.
 *   - John, 23 Sep: a brand-new driver who never saw the instructions at all,
 *     because once it became a banner on the route screen it sat behind the
 *     briefing and the face photo — screens a new driver has not passed yet.
 *
 * So: shown IN FULL exactly once per phone, as part of first-run setup, with a
 * visible way past it. After that it is only ever the banner. Seen once,
 * never a wall.
 */
const SHOWN_KEY = 'jw.installShown';

export function installEverShown(): boolean {
  try { return localStorage.getItem(SHOWN_KEY) === '1'; } catch { return true; }
}
function markInstallShown(): void {
  try { localStorage.setItem(SHOWN_KEY, '1'); } catch { /* fine */ }
}

/** Was it already put off today? Asked once a day, not once ever. */
function skippedToday(): boolean {
  try {
    return localStorage.getItem(SKIPPED_KEY) === new Date().toDateString();
  } catch { return false; }
}

/**
 * Should the gate be shown at all?
 *
 * EVERYWHERE, including iPhones -- and the iPhone screen has to carry itself,
 * because there is no button to press there and there never will be. Apple does
 * not let a website trigger installation.
 *
 * This was briefly Android-only, on the reasoning that four iPhone drivers
 * could be shown in person. The owner rejected that and was right to: he is
 * hiring, so a rule that works at four people and fails at forty is not a rule,
 * it is a chore that grows. Everything here has to survive the fleet doubling
 * without anyone standing next to a phone.
 *
 * So the iPhone path is PICTURES. Drivers who read either language with
 * difficulty do not need the phrase "the Share button" explained, they need to
 * see the thing they are looking at on their own screen. Each step draws the
 * actual bar or row, with the target ringed. The words are a caption, not the
 * instruction.
 */
export function needsInstall(): boolean {
  return !isInstalled() && !skippedToday();
}

/*
 * The browser's install offer, caught at module level because it fires once
 * and early -- waiting for a component to mount misses it entirely.
 *
 * A `canPromptInstall` flag lived here too, from when the gate was briefly
 * Android-only. The gate now shows everywhere, so nothing read it.
 */
let heldEvent: InstallEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    heldEvent = e as InstallEvent;
  });
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function InstallGate(props: { onDone: () => void; mandatory?: boolean }) {
  const [event, setEvent] = useState<InstallEvent | null>(heldEvent);
  const [checked, setChecked] = useState(false);

  // Recorded on sight, not on completion: a driver who reads it and taps past
  // has been shown it, and must not meet it again tomorrow morning.
  useEffect(() => { markInstallShown(); }, []);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      heldEvent = e as InstallEvent;
      setEvent(e as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const skip = () => {
    try { localStorage.setItem(SKIPPED_KEY, new Date().toDateString()); } catch { /* fine */ }
    reportError('install_skipped', 'install', isIOS() ? 'ios' : 'android');
    props.onDone();
  };

  return (
    <main className="screen">
      <h1>{t('installTitle')}</h1>
      <p>{props.mandatory ? t('installRequired') : t('installWhy')}</p>
      {props.mandatory && <StillSending />}

      {event ? (
        <button className="btn primary big" onClick={() => {
          void (async () => {
            try {
              await event.prompt();
              const { outcome } = await event.userChoice;
              if (outcome === 'accepted') props.onDone();
            } catch { /* they can try again */ }
          })();
        }}>
          {t('installNow')}
        </button>
      ) : props.mandatory && !isIOS() ? (
        /* Android with no install offer: usually it is ALREADY installed. */
        <p className="hint">{t('installAndroidManual')}</p>
      ) : (
        <>
          {/*
            * iPhone. Apple allows no install button, so these are the actual
            * taps, in order, in the driver's language.
            */}
          {/*
            * DRAWN, NOT DESCRIBED.
            *
            * The first version of this screen was three sentences, and the
            * owner's reaction was the right one: drivers will ring up asking
            * what it means. Several of this fleet read English and Spanish
            * only with difficulty, and "press the Share button" is meaningless
            * until you know which of the icons at the bottom IS the share
            * button.
            *
            * So step one shows the actual glyph, at the size it appears on the
            * phone, pointing down at where it lives. The words are there for
            * whoever wants them and carry none of the weight.
            */}
          {/*
            * THREE PICTURES OF WHAT IS ON THEIR SCREEN.
            *
            * Not a description of it. "Press the Share button" assumes the
            * driver already knows which of five icons that is, and the owner's
            * point was exactly this -- that sentence loses most people on the
            * first line.
            *
            * So each step draws the bar, the row and the Add button, with the
            * target ringed, so a driver can hold this screen next to their own
            * and match the shapes without reading a word.
            *
            * THESE ARE SKETCHES, NOT SCREENSHOTS, AND THAT IS A KNOWN WEAKNESS.
            * They were drawn from memory and the owner rightly challenged them.
            * Two things genuinely vary:
            *
            *   - the share icon is at the BOTTOM when Safari's address bar is
            *     at the bottom, and at the TOP RIGHT when it is at the top. The
            *     caption now says so rather than pretending otherwise.
            *   - this fleet runs iOS 15.8 through 26.6, which is a decade of
            *     Safari redesigns. One drawing cannot be accurate for all.
            *
            * The honest fix is real screenshots from a real handset, dropped in
            * as images. They are not something this code can produce. When they
            * arrive, replace these three <svg> blocks with <img> and delete
            * this note.
            */}
          <ol className="install-steps">
            <li>
              <p>{t('installStep1')}</p>
              {/*
                * THE REAL SCREENSHOT, not a drawing of one.
                *
                * Three hand-drawn attempts at this were all rejected, correctly:
                * they were approximations from memory and they did not look like
                * the phone. The owner supplied a photograph of the actual bar
                * with the button ringed, which is worth more than any icon I can
                * draw -- a driver matches the picture to their screen without
                * reading anything.
                *
                * Bundled with the app, not linked, so it is there in a basement
                * with no signal like everything else on this screen.
                */}
              <img className="ios-shot" src="/install/share.png" alt=""
                   width={600} height={230} loading="eager" />
            </li>

            <li>
              <p>{t('installStep2')}</p>
              {/*
                * The share sheet as it really is: a LIST, with the icon on the
                * right of each row, and the one we want ringed in red. The
                * earlier version drew a single isolated row, which is not what
                * a driver sees and is the reason they would not find it.
                *
                * Only the target row is labelled. The rows above and below are
                * drawn as gray bars on purpose -- they give the list its shape
                * without adding words to read, and their real labels differ
                * between iOS versions anyway.
                *
                * Replace with the owner's screenshot when it is saved; the
                * markup is one <img> away, exactly like step 1.
                */}
              <svg className="ios-shot" viewBox="0 0 300 150" role="img"
                   aria-label={t('installStep2')}>
                <rect x="0" y="0" width="300" height="150" rx="14" fill="#ffffff" />

                {/* rows above, unlabelled */}
                <line x1="18" y1="34" x2="282" y2="34" stroke="#e5e5e5" strokeWidth="2" />
                <rect x="18" y="14" width="120" height="8" rx="4" fill="#d8d8d8" />
                <rect x="258" y="10" width="20" height="16" rx="3" fill="none"
                      stroke="#c2c2c2" strokeWidth="2" />

                <line x1="18" y1="66" x2="282" y2="66" stroke="#e5e5e5" strokeWidth="2" />
                <rect x="18" y="46" width="100" height="8" rx="4" fill="#d8d8d8" />
                <rect x="258" y="42" width="20" height="16" rx="3" fill="none"
                      stroke="#c2c2c2" strokeWidth="2" />

                {/* THE ROW THAT MATTERS */}
                <text x="18" y="100" fontSize="15" fill="#000000">
                  {t('installRowLabel')}
                </text>
                <rect x="256" y="84" width="24" height="24" rx="5" fill="none"
                      stroke="#000000" strokeWidth="2.5" />
                <path d="M268 90 L268 102 M262 96 L274 96" stroke="#000000"
                      strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="268" cy="96" r="25" fill="none" stroke="#FF914C"
                        strokeWidth="5" />

                {/* a row below, unlabelled */}
                <line x1="18" y1="118" x2="282" y2="118" stroke="#e5e5e5" strokeWidth="2" />
                <rect x="18" y="130" width="70" height="8" rx="4" fill="#d8d8d8" />
              </svg>
            </li>

            <li>
              <p>{t('installStep3')}</p>
              {/*
                * The owner's own screenshot of this app being added, cropped to
                * the part that matters and with the Add button ringed in brand
                * coral. His phone is in dark mode; a driver in light mode sees
                * the same layout in light colors, and the shapes and the words
                * are what they are matching on.
                *
                * Worth keeping for a second reason: it shows the JW Express
                * icon and the real address, so a driver can see they are
                * installing the right thing.
                */}
              <img className="ios-shot" src="/install/add.png" alt=""
                   width={750} height={483} loading="eager" />
            </li>

            <li>
              <p>{t('installStep4')}</p>
              {/*
                * THE STEP THAT WAS MISSING, and the owner found it by accident.
                *
                * He installed the app while an old Safari tab was still open
                * from days earlier. Both ran at once on an iPhone 6, iOS took
                * the memory back, and the one it killed was the TAB -- which
                * reported itself as a build two versions old, hours after the
                * new one was live.
                *
                * A forgotten tab is not harmless. It keeps running, keeps
                * holding memory on a phone that cannot spare it, and keeps
                * calling the server as a build nobody is looking at any more.
                * Install and then leave the tab open and you are running two
                * copies of this app all day.
                */}
            </li>
          </ol>
          <button className="btn primary big" onClick={() => {
            // Opening from the home screen is a fresh context that passes the
            // check by itself. This is for the driver who did it and came back
            // to the tab wondering why it still shows.
            if (isInstalled()) { props.onDone(); return; }
            setChecked(true);
          }}>
            {t('installDone')}
          </button>
          {checked && <p className="hint">{t('installOpenFromIcon')}</p>}
        </>
      )}

      {props.mandatory
        ? <p className="hint">{t('installCallOffice')}</p>
        : <button className="btn link" onClick={skip}>{t('installCannot')}</button>}
    </main>
  );
}

/*
 * Work saved in THIS browser before the lock arrived. On an iPhone the
 * home-screen app has its own storage and cannot see it, so the tab has to
 * stay open until it is sent -- the queue keeps running behind this screen.
 */
function StillSending() {
  const [n, setN] = useState(0);
  useEffect(() => {
    void queueSize().then(setN).catch(() => {});
    return onQueueChange((left) => setN(left));
  }, []);
  if (n === 0) return null;
  return <div className="banner warn-banner">{t('installStillSending', { n })}</div>;
}

/** Route and stop screens only run from the home-screen icon (5 Oct). */
export function InstallLock({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (isInstalled()) return;
    try {
      const k = 'jw.installLockedOn';
      if (localStorage.getItem(k) === new Date().toDateString()) return;
      localStorage.setItem(k, new Date().toDateString());
    } catch { /* still report */ }
    reportError('install_skipped', 'install_lock', isIOS() ? 'ios:locked' : 'android:locked');
  }, []);
  if (isInstalled()) return <>{children}</>;
  return <InstallGate mandatory onDone={() => window.location.reload()} />;
}

/*
 * Home screen or browser tab, recorded once a day for each (5 Oct). Until now
 * only a phone's LATEST state was kept, so "when did he go back to the
 * browser" had no answer. Keyed per context: on Android the installed app and
 * Chrome share storage, and one must not silence the other.
 */
export function recordInstallToday(): void {
  const where = isInstalled() ? 'home' : 'tab';
  const k = `jw.installSeen.${where}`;
  const today = new Date().toDateString();
  try {
    if (localStorage.getItem(k) === today) return;
    localStorage.setItem(k, today);
  } catch { return; }
  reportError('diag_install', 'start', JSON.stringify({ where, os: isIOS() ? 'ios' : 'android', path: location.pathname }));
}
